import { Button, Container, Grid, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect } from 'react';
import { X } from 'tabler-icons-react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import {
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import { BasicBranch } from '../../type/select.types';

interface Props {
  filterValues: UseFormReturnType<UserManagementFilterForm>;
  filterSelectData: UseFormReturnType<UserManagementFilterData>;
  setSubmitApplied: React.Dispatch<React.SetStateAction<boolean>>;
}

function MerchantUserManagementFilter(props: Props) {
  const { filterSelectData, filterValues, setSubmitApplied } = props;
  const axiosPrivate = useAxiosPrivate();

  const selectLoadingData = useForm({
    initialValues: {
      isMerchantsLoading: false,
      isBranchesLoading: false,
    },
  });

  const form = useForm({
    initialValues: filterValues.values,
  });

  const disableFields = !form.values.merchantBranch;

  useEffect(() => {
    const laodBranches = async () => {
      selectLoadingData.setFieldValue('isBranchesLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/branches');
        const branches = response.data?.items?.map((partner: BasicBranch) => {
          return {
            label: partner.branchName,
            value: partner.branchId.toString(),
          };
        });
        filterSelectData.setFieldValue('merchantBranchesData', branches);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-branches-data',
          message:
            error.error ||
            err.response?.data?.error ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isBranchesLoading', false);
      }
    };

    //so that partners isn't loaded every time component
    //remounts when it comes back from transaction details
    // if (isLoadPartners) {
    //   loadPartners();
    // } else {
    //   setIsLoadPartners(true);
    // }

    laodBranches();
  }, []);

  const handleSubmit = () => {
    filterValues.setValues(form.values);
    setSubmitApplied((prev) => {
      return !prev;
    });
  };

  useEffect(() => {
    //if merchant branch filter is cleared, create new request
    //with no filters
    if (form.values.merchantBranch) {
      return;
    }
    filterValues.setValues(form.values);
    setSubmitApplied((prev) => {
      return !prev;
    });

    return () => {};
  }, [form.values.merchantBranch]);

  return (
    <>
      <Container p='lg' fluid>
        <Grid>
          <ResponsiveCol>
            <Select
              label='Select Merchant Branch'
              data={
                selectLoadingData.values.isBranchesLoading
                  ? [loadingData]
                  : filterSelectData.values.merchantBranchesData?.length === 0
                  ? [noData]
                  : filterSelectData.values.merchantBranchesData
              }
              value={
                selectLoadingData.values.isBranchesLoading
                  ? loadingData
                  : filterSelectData.values.merchantBranchesData?.length === 0
                  ? noData
                  : form.values.merchantBranch
              }
              disabled={
                selectLoadingData.values.isBranchesLoading ||
                filterSelectData.values.merchantBranchesData?.length === 0
              }
              onChange={(value: string) =>
                form.setFieldValue('merchantBranch', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isBranchesLoading ||
                  filterSelectData.values.merchantBranchesData?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        </Grid>
        <Button
          mt='lg'
          type='submit'
          disabled={disableFields}
          onClick={handleSubmit}
        >
          Apply
        </Button>
      </Container>
    </>
  );
}

export default MerchantUserManagementFilter;

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col xs={12} sm={6}>
      {children}
    </Grid.Col>
  );
};

const noData = 'No data';
const loadingData = 'Loading...';
